import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Briefcase, Palette, Coffee, Compass } from 'lucide-react';
import BrandMark from '../components/BrandMark';
import { useAuth } from '../contexts/AuthContext';

const FOCUS_OPTIONS = [
  { key: 'work', label: 'For work', desc: 'Emails, reports, analysis, presentations', icon: Briefcase },
  { key: 'creative', label: 'For creative projects', desc: 'Writing, brainstorming, design ideas', icon: Palette },
  { key: 'daily', label: 'For daily life', desc: 'Planning, research, getting things done', icon: Coffee },
  { key: 'curious', label: 'Just curious', desc: 'Explore what AI can do', icon: Compass },
];

export default function OnboardingPage() {
  const { profile, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [focus, setFocus] = useState('');
  const [readingLevel, setReadingLevel] = useState<'standard' | 'simple'>(profile?.reading_level || 'standard');
  const [saving, setSaving] = useState(false);

  const finish = async () => {
    setSaving(true);
    await updateProfile({
      account_type: 'adult',
      role: focus,
      reading_level: readingLevel,
      experience_level: profile?.experience_level || 'beginner',
      weekly_goal_minutes: profile?.weekly_goal_minutes || 60,
      onboarding_completed: true,
    });
    setSaving(false);
    navigate('/learn');
  };

  return (
    <div className="min-h-screen bg-warm-50 px-6 py-12">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <BrandMark tone="dark" size={26} />
        </div>

        <div className="flex gap-2 mb-10">
          {[0, 1].map((i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-colors duration-300 ${i <= step ? 'bg-accent-500' : 'bg-ink-100'}`} />
          ))}
        </div>

        {step === 0 && (
          <div>
            <h1 className="font-display text-4xl mb-2">What do you want to use AI for?</h1>
            <p className="text-ink-600 mb-8 text-lg">Pick a focus. You can switch any time.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {FOCUS_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setFocus(opt.key)}
                  className={`text-left p-5 rounded-2xl border transition group ${
                    focus === opt.key
                      ? 'border-accent-500 bg-white shadow-soft'
                      : 'border-ink-100 bg-white hover:border-ink-200'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-xl grid place-items-center transition ${
                      focus === opt.key ? 'bg-accent-100 text-accent-700' : 'bg-warm-100 text-ink-500 group-hover:bg-warm-200'
                    }`}>
                      <opt.icon className="w-5 h-5" />
                    </div>
                    <div className="font-medium text-lg">{opt.label}</div>
                  </div>
                  <div className="text-sm text-ink-500 leading-relaxed">{opt.desc}</div>
                </button>
              ))}
            </div>
            <div className="mt-8 flex justify-end">
              <button onClick={() => setStep(1)} disabled={!focus} className="btn-primary disabled:opacity-50">
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <h1 className="font-display text-4xl mb-2">How do you like to learn?</h1>
            <p className="text-ink-600 mb-8 text-lg">Pick the style that fits you. Switch any time in settings.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <button
                onClick={() => setReadingLevel('standard')}
                className={`text-left p-6 rounded-2xl border transition ${
                  readingLevel === 'standard'
                    ? 'border-accent-500 bg-white shadow-soft'
                    : 'border-ink-100 bg-white hover:border-ink-200'
                }`}
              >
                <div className="font-display text-xl mb-1">Standard</div>
                <div className="text-sm text-ink-500 leading-relaxed">
                  Full sentences, normal vocabulary. Good for most people.
                </div>
              </button>
              <button
                onClick={() => setReadingLevel('simple')}
                className={`text-left p-6 rounded-2xl border transition ${
                  readingLevel === 'simple'
                    ? 'border-accent-500 bg-white shadow-soft'
                    : 'border-ink-100 bg-white hover:border-ink-200'
                }`}
              >
                <div className="font-display text-xl mb-1">Plain language</div>
                <div className="text-sm text-ink-500 leading-relaxed">
                  Short sentences, friendly examples. Great if you prefer it simple.
                </div>
              </button>
            </div>
            <div className="mt-8 flex justify-between">
              <button onClick={() => setStep(0)} className="btn-ghost">Back</button>
              <button onClick={finish} disabled={saving} className="btn-accent disabled:opacity-50">
                {saving ? 'Starting…' : <>Start your first challenge <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
